import Vue from 'vue';
import Vuex from 'vuex';
import qs from "qs";
import router from '@/router';
import { MinichatLine } from '@/itemtypes';
import { BACKEND_URL } from "@/constants";

Vue.use(Vuex);

const LOGIN_URL = BACKEND_URL + "/login";
const LOGOUT_URL = BACKEND_URL + "/logout";
const ITEMS_URL = BACKEND_URL + "/list/";
const LIST_URL = BACKEND_URL + '/list/'; // POST to /list/TYPENAME/
const UNIVERSE_URL = BACKEND_URL + "/universe/";
const MINICHAT_URL = BACKEND_URL + '/list/minichat%20line/';
const MINICHAT_LINES_URL = MINICHAT_URL + '?limit=100';
const GOGGLE_URL = BACKEND_URL + '/list/goggle/?limit=1';
const GOGGLE_ENTER_WORD_URL = BACKEND_URL + '/goggleword';

const ANON_USER = {
    anon: true,
    admin: false
};

let oldDesignCssLoaded = false;

export default new Vuex.Store({
    state: {
        // feedback area
        userFeedback: '',

        // account stuff
        user: null as any,

        // item list
        displayItemsData: null as any,
        items: [] as any[],
        forwardUrl: '',
        backUrl: '',

        // universe
        universeItem: null as any,

        // minichat
        minichatLines: [] as MinichatLine[],

        goggle: null as any,

        // settings
        comicMode: false,
        design: 0,

    },

    mutations: {

        toggleComicMode(state) {
            state.comicMode = !state.comicMode;
        },

        incrementDesign(state) {
            state.design = (state.design + 1) % 4;
        },

        setUserFeedback(state, data: { message: string }) {
            state.userFeedback = data ? data.message : '';
        },

        setUser(state, user: any) {
            state.user = user;
        },

        setItems(state, data: { items: any[], forwardUrl: string, backUrl: string, displayItemsData: any }) {
            state.items = data.items;
            state.forwardUrl = data.forwardUrl;
            state.backUrl = data.backUrl;
            state.displayItemsData = data.displayItemsData;
        },

        itemDeleted(state, data: { id: any }) {
            state.items = state.items.filter( (item) => item.id !== data.id);
            if (state.universeItem && state.universeItem.id === data.id) {
                state.universeItem = null;
            }
            state.minichatLines = state.minichatLines.filter( (item) => item.id !== data.id);
        },

        setEditingItem(state, data: { id: number, editing: boolean, updated?: any }) {
            function update(item: any, editing: boolean, updated?: any) {
                if (updated) {
                    return { ...data.updated, _editing: data.editing };
                } else {
                    Vue.set(item, '_editing', data.editing);
                    return item;
                }
            }

            function updateArray(arr: any[]) {
                return arr.map( (i: any) => {
                    if (i.id === data.id)
                        return update(i, data.editing, data.updated);
                    return i;
                });
            }

            state.items = updateArray(state.items);
            if (state.universeItem && state.universeItem.id === data.id)
                state.universeItem = update(state.universeItem, data.editing, data.updated);
            if (state.goggle && state.goggle.id === data.id)
                state.goggle = update(state.goggle, data.editing, data.updated);
            state.minichatLines = updateArray(state.minichatLines);
        },

        setUniverseItem(state, data: any) {
            state.universeItem = data;
        },

        setMinichatLines(state, lines: MinichatLine[]) {
            state.minichatLines = lines;
        },

        addMinichatLine(state, line: any) {
            state.minichatLines.push(line);
        },

        setGoggle(state, goggle: any) {
            state.goggle = goggle;
            state.items = state.items.map( (i: any) => i.id === goggle.id ? goggle : i);
        },

    },

    actions: {
        loadGoggle({ commit, state }) {
            if (!state.goggle) {
                Vue.axios.get(GOGGLE_URL).then((response: { data: any }) => {
                    commit('setGoggle', response.data.items[0]);
                });
            }
        },

        enterGoggleWord(context, word: string) {
            context.dispatch('loadGoggle').then(() => {
                Vue.axios.post(
                    GOGGLE_ENTER_WORD_URL,
                    qs.stringify({
                        word,
                    }),
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        }
                    }).then((response: { data: any }) => {
                        if (response.data.error) {
                            context.dispatch('showUserFeedback', { message: response.data.error });
                        } else {
                            context.commit('setGoggle', response.data.goggle);
                        }

                });
            });
        },

        nextDesign(context) {

            if (!oldDesignCssLoaded) {
                oldDesignCssLoaded = true;
                require("@/old-designs/old-design.scss");  // common 'css reset' rules for old designs
                require("@/old-designs/design-2001.scss");
                require("@/old-designs/design-2005.scss");
                require("@/old-designs/design-2007.scss");
            }
            
            context.commit('incrementDesign');
            document.body.classList.remove('old-design', 'design-2001', 'design-2005', 'design-2007');
            switch (context.state.design) {
            case 0:
                // default design, no extra classes;
                context.dispatch('showUserFeedback', { message: 'Welcome back!' });
                break;
            case 1:
                // 2007
                document.body.classList.add('design-2007', 'old-design');
                context.dispatch('showUserFeedback', { message: 'Welcome to 2007!' });
                break;
            case 2:
                // 2005
                document.body.classList.add('design-2005', 'old-design');
                context.dispatch('showUserFeedback', { message: 'Welcome to 2005!' });
                break;
            case 3:
                // 2001
                document.body.classList.add('design-2001', 'old-design');
                context.dispatch('showUserFeedback', { message: 'Welcome to 2001!' });
                break;
            }
            window.scrollTo(0, 0);
        },

        loadMinichat({ commit }) {
            Vue.axios.get(MINICHAT_LINES_URL).then((response: { data: any }) => {
                const lines: any[] = response.data.items.slice().reverse();
                let prev: number = -1;
                lines.forEach( (line) => {
                    line.poster.same = line.poster.id === prev;
                    prev = line.poster.id;
                });
                commit('setMinichatLines', lines);
            });
        },

        postToMinichat({ commit }, data: { message: string }) {
            Vue.axios.post(
                MINICHAT_URL,
                qs.stringify({
                    prop_name: data.message,
                }),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }
                }
            ).then((response: { data: any }) => {
                commit('addMinichatLine', response.data.item);
            });
        },

        loadUniverseItem({ commit }, data: { subject: string }) {
            const url = UNIVERSE_URL + encodeURIComponent(data.subject);
            Vue.axios.get(url).then((response: { data: any }) => {
                commit('setUniverseItem', response.data.item);
            });
        },

        showUserFeedback({ commit, state }, data: { message: string, time: number }) {
            const time = data.time === undefined ? 6 : data.time;
            commit('setUserFeedback', data);
            if (time > 0) {
                setTimeout(() => {
                    if (state.userFeedback === data.message) {
                        // Message  hasn't changed. Clear it now.
                        commit('setUserFeedback');
                    }
                }, 6000);
            }
        },

        updateItems({ commit, state }, data: { params: Record<string, string>, isQwokPosts: boolean }) {
            if (!data)
                data = state.displayItemsData;
            if (!data) {
                window.console.log('updateItems: no parameters');
                return;
            }
            const params = data.params;
            const queryString = Object.keys(params).map( (key) => key + '=' + params[key]).join('&');
            Vue.axios.get(ITEMS_URL + "?" + queryString).then((response: { data: any }) => {

                const isQwokPosts = data.isQwokPosts;
                document.body.classList.remove(isQwokPosts ? 'gwtp' : 'qwok');
                document.body.classList.add(isQwokPosts ? 'qwok' : 'gwtp');

                let forwardUrl = response.data.forward_url ? response.data.forward_url : '';
                forwardUrl = forwardUrl.replace(/^.+\?/, "/items?");
                let backUrl = response.data.back_url ? response.data.back_url : '';
                backUrl = backUrl.replace(/^.+\?/g, "/items?");
                commit('setItems', {
                    items: response.data.items,
                    forwardUrl,
                    backUrl,
                    displayItemsData: data
                });
            });
        },

        deleteItem({ commit, state }, data: { id: any }) {
            Vue.axios.delete(
                `${BACKEND_URL}/${data.id}`,
            ).then((response: { data: any }) => {
                commit('itemDeleted', { id: data.id });
                this.dispatch('showUserFeedback', { message: `Item ${data.id} deleted.`});
                if (state.items.length === 0) {
                    this.dispatch('updateItems');
                }
            }).catch((error) => {
                window.console.log(`error deleting ${data.id}`);
                window.console.log(error);
            });
        },

        createItem({ commit }, data: { type: string, props: Record<string, string> }) {
            const formData = new FormData();
            for (const [key, value] of Object.entries(data.props)) {
                formData.append(key, value);
            }

            const LIST_TYPE_URL = LIST_URL + `/${data.type}/`;
            Vue.axios.post(
                LIST_TYPE_URL,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            ).then((response: { data: any }) => {
                router.push({ 
                    path: '/items',
                    query: {
                        types: data.type,
                        limit: '1',
                        from: response.data.item.time
                    },
                });
                this.dispatch('showUserFeedback', { message: `${data.type} item created.` });
            });
        },

        editItem({ commit }, data: { id: number, editing: boolean }) {
            commit('setEditingItem', data);
        },

        updateItem({ commit }, data: { id: number, props: Record<string, string> }) {
            const formData = new FormData();
            for (const [key, value] of Object.entries(data.props)) {
                formData.append(key, value);
            }

            Vue.axios.post(
                `${BACKEND_URL}/${data.id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            ).then((response: { data: any }) => {
                if (response.data.error) {
                    this.dispatch('showUserFeedback', { message: response.data.error, time: -1 });
                } else {
                    const item = response.data.item;
                    commit('setEditingItem', { id: item.id, editing: false, updated: item });
                    /*
                    router.push({
                        path: '/items',
                        query: {
                            types: item.itemType,
                            limit: '1',
                            from: item.time
                        },
                    });*/
                    this.dispatch('showUserFeedback', { message: `${item.itemType} item ${item.id} updated.` });
                }
            }).catch( (error) => {
                window.console.log('ERROR', error);
                this.dispatch('showUserFeedback', { message: 'Unexpected error occurred: ' + error, time: -1 });
            });
        },

        logIn({ commit }, data: { username?: string, pass?: string }) {
            Vue.axios.post(
                LOGIN_URL,
                qs.stringify({
                    ajax: 1,
                    username: data.username,
                    pass: data.pass,
                }),
                {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded"
                    }
                }
            )
            .then((response: { data: any }) => {
                if (response.data.user)
                    commit("setUser", response.data.user);
                else {
                    if (response.data.error)
                        this.dispatch('showUserFeedback', { message: response.data.error });
                    commit("setUser", ANON_USER);
                }
            });
        },

        logOut({ commit }) {
            Vue.axios.post(
                LOGOUT_URL,
                qs.stringify({
                    ajax: 1
                }),
                {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded"
                    }
                }
            )
            .then((response: { data: any }) => {
                commit("setUser", ANON_USER);
            });
        }
    },

});
